/**
 * Device Certification — IEC 62304 + IEEE 11073 conformance
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { FileCheck, Shield, CheckCircle2, XCircle, AlertCircle, Award } from "lucide-react";
import { getMockProfiles } from "@/lib/device/profiles";

export const Route = createFileRoute("/integration/device-certification")({
  component: DeviceCertification,
});

interface CertCheck {
  id: string;
  label: string;
  standard: "IEC 62304" | "IEC 11073" | "ISO 14971" | "ISO 13485";
  description: string;
  status: "PASS" | "FAIL" | "WARNING" | "PENDING";
  evidence?: string;
}

const STANDARDS: CertCheck[] = [
  { id: "srs", label: "Software Requirements Specification", standard: "IEC 62304", description: "SRS document reviewed + approved", status: "PASS", evidence: "DOC-SRS-v3.2.pdf" },
  { id: "sds", label: "Software Design Specification", standard: "IEC 62304", description: "Architecture + detailed design", status: "PASS", evidence: "DOC-SDS-v3.2.pdf" },
  { id: "verification", label: "Software Verification", standard: "IEC 62304", description: "Unit + integration + system tests", status: "PASS", evidence: "TEST-VER-v3.2.pdf" },
  { id: "risk", label: "Risk Management File", standard: "ISO 14971", description: "Risk analysis + residual risk evaluation", status: "PASS", evidence: "RISK-v3.2.pdf" },
  { id: "qms", label: "Quality Management System", standard: "ISO 13485", description: "QMS audit (annual)", status: "PASS", evidence: "QMS-AUDIT-2025.pdf" },
  { id: "11073-dim", label: "DIM — Domain Information Model", standard: "IEC 11073", description: "Conformance to IEEE 11073-10201", status: "PASS" },
  { id: "11073-nom", label: "NOM — Nomenclature", standard: "IEC 11073", description: "Conformance to IEEE 11073-10101", status: "PASS" },
  { id: "11073-mds", label: "MDS — Medical Device System", standard: "IEC 11073", description: "Agent + manager communication", status: "WARNING", evidence: "Partial: 87% coverage" },
  { id: "cybersec", label: "Cybersecurity (IEC 81001-5-1)", standard: "IEC 62304", description: "Threat model + security testing", status: "PASS", evidence: "SEC-THREAT-MODEL.pdf" },
  { id: "postmarket", label: "Post-Market Surveillance", standard: "ISO 14971", description: "PMS plan + vigilance reporting", status: "PENDING", evidence: "PMS plan in draft" },
];

function DeviceCertification() {
  const q = useQuery({
    queryKey: ["device-certification"],
    queryFn: async () => {
      await new Promise((r) => setTimeout(r, 200));
      return { profiles: getMockProfiles(), checks: STANDARDS };
    },
  });

  const passed = STANDARDS.filter((c) => c.status === "PASS").length;
  const total = STANDARDS.length;
  const passRate = Math.round((passed / total) * 100);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileCheck className="h-5 w-5" /> Device Certification
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          IEC 62304 (software lifecycle) · IEC 11073 (medical device communication) · ISO 14971 (risk) · ISO 13485 (QMS)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Conformité globale</div>
            <div className="mt-2 text-3xl font-bold text-emerald-400">{passRate}%</div>
            <Progress value={passRate} className="mt-2 h-1" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Standards couverts</div>
            <div className="mt-2 text-3xl font-bold">4</div>
            <div className="text-xs text-muted-foreground">62304 · 11073 · 14971 · 13485</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Profiles évalués</div>
            <div className="mt-2 text-3xl font-bold">{q.data?.profiles.length ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Prochaine revue</div>
            <div className="mt-2 text-lg font-bold">2026-12-15</div>
            <div className="text-xs text-muted-foreground">Audit annuel</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield className="h-4 w-4" /> Checklist de conformité</CardTitle>
          <CardDescription>10 contrôles par standard · FDA / CE ready</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {q.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            STANDARDS.map((c) => (
              <div key={c.id} className="flex items-start gap-3 rounded-md border border-white/5 bg-white/[0.02] p-3">
                <div className="mt-0.5">
                  {c.status === "PASS" ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> :
                    c.status === "FAIL" ? <XCircle className="h-4 w-4 text-red-400" /> :
                      c.status === "WARNING" ? <AlertCircle className="h-4 w-4 text-amber-400" /> :
                        <AlertCircle className="h-4 w-4 text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{c.label}</span>
                    <Badge variant="outline" className="text-[9px]">{c.standard}</Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{c.description}</div>
                  {c.evidence && (
                    <div className="mt-1 text-[11px] text-muted-foreground/70 font-mono">📎 {c.evidence}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
